import { useMemo, useState } from "react";
import type { SurfDeck, SurfStyle } from "@vida/surf-core";
import { StyleStep } from "../wizard/StyleStep";

/** Contextual preview seed from the existing deck (real slide text > outline > context). */
function deckPreviewSeed(deck: SurfDeck): { title: string; subtitle?: string } {
  const slide =
    deck.slides.find((s) =>
      s.layers.some((l) => l.type === "text" && l.textRole === "title" && l.content?.trim()),
    ) ?? deck.slides[0];
  const layerText = (role: string) =>
    slide?.layers.find((l) => l.type === "text" && l.textRole === role)?.content?.trim();
  const title =
    layerText("title") ||
    deck.outline?.[0]?.title?.trim() ||
    deck.deckContext?.summary?.trim() ||
    deck.title;
  const subtitle = layerText("subtitle") || deck.outline?.[0]?.subtitle?.trim() || undefined;
  return { title, subtitle };
}

type Props = {
  deck: SurfDeck;
  /** Apply the new theme to the existing deck (non-destructive: slides/outline kept). */
  onApply: (theme: SurfStyle, opts: { regenerateBackgrounds: boolean }) => void;
  onClose: () => void;
};

/**
 * Re-open the Style step from the editor to change the deck theme while keeping
 * the already-generated structure (slides, outline, layout, text). Optionally
 * regenerates backgrounds with the new style.
 */
export function EditorStylePanel({ deck, onApply, onClose }: Props) {
  const [draft, setDraft] = useState<SurfStyle>(deck.theme);
  const [confirmed, setConfirmed] = useState(false);
  const [regen, setRegen] = useState(true);
  const previewSeed = useMemo(() => deckPreviewSeed(deck), [deck]);

  return (
    <div className="editor-style-overlay" role="dialog" aria-label="Cambia stile">
      <div className="editor-style-overlay-panel">
        <header className="editor-style-overlay-head">
          <h2>Stile</h2>
          <p className="muted">
            Cambia lo stile mantenendo la struttura già generata (slide, outline, testi).
          </p>
          <button type="button" className="surf-icon-btn" aria-label="Chiudi" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="editor-style-overlay-body">
          <StyleStep
            style={draft}
            presentationType={draft.presentationType ?? ""}
            previewSeed={previewSeed}
            onStyleChange={(s) => {
              setDraft(s);
              setConfirmed(false);
            }}
            onStyleConfirmed={setConfirmed}
          />
        </div>

        <footer className="editor-style-overlay-foot">
          <label className="row">
            <input
              type="checkbox"
              checked={regen}
              onChange={(e) => setRegen(e.target.checked)}
            />
            Rigenera gli sfondi con il nuovo stile
          </label>
          <div className="wizard-actions">
            <button type="button" onClick={onClose}>
              Annulla
            </button>
            <button
              type="button"
              className="primary-btn"
              disabled={!confirmed}
              onClick={() => onApply(draft, { regenerateBackgrounds: regen })}
            >
              Applica stile
            </button>
          </div>
          {!confirmed && (
            <p className="muted">Conferma con «Usa stile» nell&apos;anteprima per abilitare «Applica».</p>
          )}
        </footer>
      </div>
    </div>
  );
}
